import { useLocation } from 'react-router-dom';

/**
 * Creates a context-aware hook that selects between private and public API hooks
 * based on the current route path.
 * 
 * @param privateHook The hook to use for private (authenticated) routes
 * @param publicHook The hook to use for public routes
 * @returns A function that will use either the private or public hook based on the current route
 */
export function createContextAwareHook<TArgs extends any[], TResult>(
  privateHook: (...args: TArgs) => TResult,
  publicHook: (...args: TArgs) => TResult
) {
  return function useContextAwareHook(...args: TArgs): TResult {
    const location = useLocation();
    const isPublicRoute = location.pathname.startsWith('/public');
    
    return isPublicRoute ? publicHook(...args) : privateHook(...args);
  };
}

/**
 * Example usage:
 * 
 * ```typescript
 * import { useGetProjectByIdQuery, usePublicGetProjectByIdQuery } from '../features/project/projectApiSlice';
 * 
 * export const useContextAwareGetProjectById = createContextAwareHook(
 *   useGetProjectByIdQuery,
 *   usePublicGetProjectByIdQuery
 * );
 * 
 * // Then in your component:
 * // const { data, isLoading } = useContextAwareGetProjectById(projectId);
 * ```
 */ 